import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { useMancanti } from '../../../lib/dashboard-widgets-api';
import type { MancantiItem } from '@produzionejb/types';

type Bucket = 'BARATTOLI' | 'SFUSI' | 'FUMI';
const BUCKET_ORDER: Bucket[] = ['BARATTOLI', 'SFUSI', 'FUMI'];

export function MancantiWidget() {
  const { t } = useTranslation();
  const q = useMancanti();

  const grouped = useMemo<Record<Bucket, MancantiItem[]>>(() => {
    const out: Record<Bucket, MancantiItem[]> = { BARATTOLI: [], SFUSI: [], FUMI: [] };
    for (const item of q.data?.items ?? []) {
      if (item.bucket && item.bucket !== 'OLI') {
        out[item.bucket as Bucket].push(item);
      }
    }
    return out;
  }, [q.data]);

  const totalCount = (q.data?.items ?? []).filter((i) => i.bucket && i.bucket !== 'OLI').length;
  const degenerated = q.data?.degenerated ?? false;
  const prevLabel = q.data?.previousPeriodLabel ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.mancanti.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {degenerated && (
          <p className="mb-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
            {t('dashboard.mancanti.degeneratedDisclaimer', {
              prev: prevLabel ?? t('dashboard.mancanti.previousNone'),
            })}
          </p>
        )}
        {q.isLoading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {q.isError && (
          <p className="text-sm text-red-600">{t('dashboard.mancanti.errorLoad')}</p>
        )}
        {!q.isLoading && !q.isError && totalCount === 0 && (
          <p className="text-sm text-slate-500">{t('dashboard.mancanti.empty')}</p>
        )}
        {totalCount > 0 && (
          <div className="space-y-3">
            {BUCKET_ORDER.map((b) =>
              grouped[b].length === 0 ? null : (
                <div key={b}>
                  <h4 className="mb-1 text-xs font-semibold uppercase text-slate-500">{b}</h4>
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-2 py-1 text-left font-medium text-slate-600">
                          {t('dashboard.mancanti.col.product')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.mancanti.col.grammatura')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.mancanti.col.baseline')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.mancanti.col.produced')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.mancanti.col.mancanti')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[b].map((row) => (
                        <tr
                          key={`${row.productId}::${row.grammatura}`}
                          className="border-b border-slate-100 last:border-0"
                        >
                          <td className="px-2 py-1 text-slate-700">{row.productName}</td>
                          <td className="px-2 py-1 text-right font-mono">{row.grammatura}g</td>
                          <td className="px-2 py-1 text-right font-mono">{row.baselinePezzi}</td>
                          <td className="px-2 py-1 text-right font-mono">{row.producedPezzi}</td>
                          <td
                            className={`px-2 py-1 text-right font-mono font-semibold ${
                              row.mancantiPezzi > 0 ? 'text-red-600' : 'text-emerald-600'
                            }`}
                          >
                            {row.mancantiPezzi > 0 ? `+${row.mancantiPezzi}` : row.mancantiPezzi}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ),
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
